import { cache } from 'react';
import { notFound } from 'next/navigation';
import { api } from '@weareconceptstudio/core';

export const revalidate = 3600;

// ======================== Global ======================== //
export const getGlobal = cache(async ({ lang }) => {
	try {
		const data = await api.get({ url: 'global-data', lang });

		// Ensure we return a valid object even if API fails
		if (!data || typeof data !== 'object') {
			console.warn('Global data API returned invalid data, using fallback');
			return {};
		}

		return data;
	} catch (error) {
		console.error('Failed to fetch global data:', error);
		// Return empty object as fallback to prevent undefined errors
		return {};
	}
});

// ======================== Page ======================== //
export const getPage = cache(async ({ slug, lang, params = {} }) => {
	return await api.get({ url: `${slug}`, lang, params }).catch((err) => {
		notFound();
	});
});

// ======================== News ======================== //
export const getNews = cache(async ({ lang, params = {} }) => {
	return await api.get({
		url: 'news',
		lang,
		params: {
			...params,
		},
	});
});

// ======================== News Individual ======================== //
export const getNewsIndividual = async ({ slug, lang, params = {} }) => {
	try {
		const data = await api.get({ url: `news/${slug}`, lang, params });

		if (!data) {
			notFound();
		}

		return data;
	} catch (err) {
		notFound();
	}
};

// ======================== Related News ======================== //
export const getRelatedNews = cache(async ({ id, lang }) => {
	try {
		const { data } = await api.get({ url: `related-news?news_id=${id}`, lang });

		if (!data) {
			notFound();
		}

		return data;
	} catch (err) {
		notFound();
	}
});

// ======================== Trusted by Leaders ======================== //
export const getTrustedByLeaders = cache(async ({ lang }) => {
	return await api.get({
		url: 'trusted-by-leaders',
		lang,
	});
});

// ======================== Industries Individual ======================== //
export const getIndustriesIndividual = async ({ slug, lang, params = {} }) => {
	try {
		const data = await api.get({ url: `industries/${slug}`, lang, params });

		if (!data) {
			notFound();
		}

		return data;
	} catch (err) {
		notFound();
	}
};

// ======================== Success Stories ======================== //
export const getStories = cache(async ({ lang, params = {} }) => {
	return await api.get({ url: `success-stories`, lang, params });
});

// ======================== Success Stories Individual ======================== //
export const getStoriesIndividual = async ({ slug, lang, params = {} }) => {
	try {
		const data = await api.get({ url: `success-stories/${slug}`, lang, params });

		if (!data) {
			notFound();
		}

		return data;
	} catch (err) {
		notFound();
	}
};

// ======================== Events ======================== //
export const getEvents = cache(async ({ lang, params = {} }) => {
	return await api.get({
		url: `events`,
		lang,
		params: {
			...params,
		},
	});
});

// ======================== Events Individual ======================== //
export const getEventsIndividual = async ({ slug, lang, params = {} }) => {
	try {
		const data = await api.get({ url: `events/${slug}`, lang, params });

		if (!data) {
			notFound();
		}

		return data;
	} catch (err) {
		notFound();
	}
};

// ======================== Related Events ======================== //
export const getRelatedEvents = cache(async ({ id, lang }) => {
	try {
		const { data } = await api.get({ url: `related-events?event_id=${id}`, lang });

		if (!data) {
			notFound();
		}

		return data;
	} catch (err) {
		notFound();
	}
});

// ======================== Regions ======================== //
export const getRegions = cache(async ({ lang }) => {
	return await api.get({
		url: 'regions',
		lang,
	});
});

// ======================== Teams ======================== //
export const getTeams = cache(async ({ lang, params = {} }) => {
	return await api.get({ url: `teams`, lang, params });
});

// ======================== Leadership ======================== //
export const getLeadership = cache(async ({ lang, params = {} }) => {
	return await api.get({ url: `leadership`, lang, params });
});

// ======================== Trustees ======================== //
export const getTrustees = cache(async ({ lang, params = {} }) => {
	return await api.get({ url: `trustees`, lang, params });
});

// ======================== Publications ======================== //
export const getPublications = cache(async ({ lang, params = {} }) => {
	return await api.get({ url: `publications`, lang, params });
});

// ======================== Publication Categories ======================== //
export const getPublicationCategories = cache(async ({ lang }) => {
	return await api.get({ url: `publication-categories`, lang });
});

// ======================== Incentives ======================== //
export const getIncentives = async ({ lang, params = {} }) => {
	try {
		const data = await api.get({ url: `incentives`, lang, params });

		if (!data) {
			notFound();
		}

		return data;
	} catch (err) {
		notFound();
	}
};

// ======================== Incentives Individual ======================== //
export const getIncentivesIndividual = async ({ slug, lang, params = {} }) => {
	try {
		const data = await api.get({ url: `incentives/${slug}`, lang, params });

		if (!data) {
			notFound();
		}

		return data;
	} catch (err) {
		notFound();
	}
};

// ======================== Representatives ======================== //
export const getRepresentatives = cache(async ({ lang }) => {
	return await api.get({
		url: 'representatives',
		lang,
	});
});

// ======================== Services ======================== //
export const getServices = cache(async ({ lang }) => {
	return await api.get({
		url: 'services',
		lang,
	});
});

// ======================== Charts ======================== //
export const getCharts = cache(async ({ lang, params = {} }) => {
	return await api.get({
		url: 'charts',
		lang,
		params: {
			...params,
		},
	});
});

// ======================== Countries ======================== //
export const getCountries = cache(async ({ lang }) => {
	return await api.get({
		url: 'countries',
		lang,
	});
});
